import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { toast } from "sonner";
import { User as UserIcon, Sliders, Bell, Users, Database, Globe, Sun, Moon, Monitor, LogOut, Download, RefreshCw, Trash2, Target, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import { getRegion, setRegion, CURRENCIES, DATE_FORMATS } from "@/lib/region";
import HouseholdPanel from "@/components/settings/HouseholdPanel";

const TABS = [
  { id: "account", label: "Account", icon: UserIcon },
  { id: "payoff", label: "Payoff & Credit", icon: Sliders },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "regional", label: "Regional", icon: Globe },
  { id: "household", label: "Household", icon: Users },
  { id: "data", label: "Data & Privacy", icon: Database },
];

export default function Settings() {
  const [tab, setTab] = useState("account");
  const [plan, setPlan] = useState(null);
  const loadPlan = () => api.get("/payoff-plan").then(r => setPlan(r.data));
  useEffect(() => { loadPlan(); }, []);

  return (
    <div className="space-y-6" data-testid="settings-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.2em] text-teal-500 font-semibold">Preferences</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Settings</h1>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible lg:sticky lg:top-6 self-start" data-testid="settings-nav">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`settings-tab-${t.id}`}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                tab === t.id ? "bg-teal-500/10 text-teal-600 dark:text-teal-300 border border-teal-500/20" : "text-muted-foreground hover:text-foreground hover:bg-accent/60"
              }`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </nav>

        <div className="min-w-0 max-w-3xl">
          {tab === "account" && <AccountSection />}
          {tab === "payoff" && <PayoffSection plan={plan} onSaved={loadPlan} />}
          {tab === "notifications" && <NotificationsSection plan={plan} onSaved={loadPlan} />}
          {tab === "regional" && <RegionalSection />}
          {tab === "household" && <HouseholdPanel />}
          {tab === "data" && <DataSection />}
        </div>
      </div>
    </div>
  );
}

function Card({ title, desc, children, testid }) {
  return (
    <section className="p-6 rounded-2xl border border-border bg-card" data-testid={testid}>
      <div className="font-display font-bold text-lg">{title}</div>
      {desc && <div className="text-xs text-muted-foreground mt-1">{desc}</div>}
      <div className="mt-5">{children}</div>
    </section>
  );
}

function AccountSection() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  return (
    <div className="space-y-4">
      <Card title="Your account" desc="You're signed in with Google. Managed by OWEverit." testid="settings-account-card">
        <div className="flex items-center gap-4">
          {user?.picture ? <img src={user.picture} alt="" className="h-14 w-14 rounded-full object-cover border border-border" />
            : <div className="h-14 w-14 rounded-full bg-gradient-to-br from-teal-400 to-indigo-500" />}
          <div className="min-w-0">
            <div className="font-semibold text-lg truncate" data-testid="settings-user-name">{user?.name}</div>
            <div className="text-sm text-muted-foreground truncate" data-testid="settings-user-email">{user?.email}</div>
          </div>
        </div>
        <button onClick={logout} data-testid="settings-logout-btn" className="mt-5 flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-rose-500/10 hover:text-rose-500 hover:border-rose-500/30 transition">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </Card>

      <Card title="Appearance" desc="Choose how OWEverit looks on this device." testid="settings-appearance-card">
        <div className="flex flex-wrap gap-3">
          <button onClick={() => setTheme("light")} data-testid="settings-theme-light"
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition ${theme === "light" ? "border-teal-500/40 bg-teal-500/10 text-teal-600" : "border-border hover:bg-accent"}`}>
            <Sun className="h-4 w-4" /> Light
          </button>
          <button onClick={() => setTheme("dark")} data-testid="settings-theme-dark"
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition ${theme === "dark" ? "border-teal-500/40 bg-teal-500/10 text-teal-300" : "border-border hover:bg-accent"}`}>
            <Moon className="h-4 w-4" /> Dark
          </button>
          <button onClick={() => setTheme("system")} data-testid="settings-theme-system"
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition ${theme === "system" ? "border-teal-500/40 bg-teal-500/10 text-teal-500" : "border-border hover:bg-accent"}`}>
            <Monitor className="h-4 w-4" /> System
          </button>
        </div>
      </Card>
    </div>
  );
}

function PayoffSection({ plan, onSaved }) {
  const [goal, setGoal] = useState(30);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (plan?.utilization_goal_pct != null) setGoal(plan.utilization_goal_pct); }, [plan]);
  const dirty = plan && parseFloat(goal) !== plan.utilization_goal_pct;

  const save = async () => {
    const v = Math.min(90, Math.max(1, parseFloat(goal) || 30));
    setBusy(true);
    try { await api.put("/payoff-plan", { utilization_goal_pct: v }); toast.success(`Utilization goal set to ${v}%`); onSaved?.(); }
    catch { toast.error("Could not save"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Card title="Credit utilization goal" desc="The target utilization OWEverit helps you stay under. Lenders reward keeping this below 30%." testid="settings-util-goal-card">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-3 flex-1 min-w-[220px]">
            <Target className="h-5 w-5 text-indigo-400 shrink-0" />
            <input type="range" min="1" max="90" step="1" value={goal} onChange={e => setGoal(e.target.value)} data-testid="settings-util-goal-slider" className="flex-1 accent-teal-500" />
            <div className="w-16 flex items-center gap-1">
              <input type="number" min="1" max="90" value={goal} onChange={e => setGoal(e.target.value)} data-testid="settings-util-goal-input"
                className="w-12 px-2 py-1 rounded-md bg-background border border-border text-sm text-right font-mono focus:outline-none focus:ring-2 focus:ring-teal-500/40" />
              <span className="text-sm text-muted-foreground">%</span>
            </div>
          </div>
          <button onClick={save} disabled={busy || !dirty} data-testid="settings-util-goal-save"
            className="px-4 py-2 rounded-lg bg-teal-500 text-white text-sm font-semibold hover:bg-teal-600 disabled:opacity-40 transition">Save goal</button>
        </div>
      </Card>

      <Card title="Payoff strategy" desc="Snowball vs Avalanche is an interactive choice — you'll see the payoff date and order change live on the Debts page." testid="settings-strategy-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm">
            Current strategy: <span className="font-semibold capitalize text-teal-500" data-testid="settings-current-strategy">{plan?.strategy || "avalanche"}</span>
          </div>
          <Link to="/debts" data-testid="settings-strategy-link" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition">
            Change on Debts <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </Card>
    </div>
  );
}

function NotificationsSection({ plan, onSaved }) {
  const [busy, setBusy] = useState(false);
  const lead = plan?.notify_lead_days ?? 3;
  const promo = plan?.notify_promo ?? true;

  const save = async (patch, msg) => {
    setBusy(true);
    try { await api.put("/payoff-plan", patch); if (msg) toast.success(msg); onSaved?.(); }
    catch { toast.error("Could not save"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Card title="Due-date reminders" desc="How many days before a bill's due date the reminder bell should light up." testid="settings-reminders-card">
        <div className="flex flex-wrap gap-2" data-testid="settings-lead-days">
          {[1, 2, 3, 5, 7].map(n => (
            <button key={n} onClick={() => save({ notify_lead_days: n }, `Reminders start ${n} day${n === 1 ? "" : "s"} ahead`)} disabled={busy}
              data-testid={`settings-lead-${n}`}
              className={`px-4 py-2 rounded-lg border text-sm font-medium transition disabled:opacity-50 ${lead === n ? "border-teal-500/40 bg-teal-500/10 text-teal-600 dark:text-teal-300" : "border-border hover:bg-accent"}`}>
              {n} day{n === 1 ? "" : "s"}
            </button>
          ))}
        </div>
      </Card>

      <Card title="Promo APR alerts" desc="Warn me when a 0%/promotional rate is about to expire so I can pay it down first." testid="settings-promo-card">
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm text-muted-foreground">Show promo-expiry notifications</div>
          <Switch checked={promo} disabled={busy} data-testid="settings-promo-toggle" className="data-[state=checked]:bg-teal-500"
            onCheckedChange={(v) => save({ notify_promo: v }, v ? "Promo alerts on" : "Promo alerts off")} />
        </div>
      </Card>

      <EmailPrefs />
    </div>
  );
}

function EmailPrefs() {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/email/prefs").then(r => setP(r.data)); }, []);
  const save = async (patch, msg) => { setP(x => ({ ...x, ...patch })); try { await api.put("/email/prefs", patch); toast.success(msg); } catch { toast.error("Failed"); } };
  if (!p) return null;
  return (
    <Card title="Email" desc="Get reminders and a weekly summary by email. Manage what lands in your inbox." testid="settings-email-card">
      <div className="flex items-center justify-between py-2">
        <div className="text-sm">Bill & promo-APR reminders</div>
        <Switch checked={p.email_reminders} data-testid="email-reminders-toggle" className="data-[state=checked]:bg-teal-500" onCheckedChange={(v) => save({ email_reminders: v }, v ? "Email reminders on" : "Email reminders off")} />
      </div>
      <div className="flex items-center justify-between py-2 border-t border-border/60">
        <div className="text-sm">Weekly money digest</div>
        <Switch checked={p.weekly_digest} data-testid="email-digest-toggle" className="data-[state=checked]:bg-teal-500" onCheckedChange={(v) => save({ weekly_digest: v }, v ? "Weekly digest on" : "Weekly digest off")} />
      </div>
    </Card>
  );
}

function RegionalSection() {
  const r = getRegion();
  const [currency, setCurrency] = useState(r.currency);
  const [dateFormat, setDateFormat] = useState(r.dateFormat);
  const dirty = currency !== r.currency || dateFormat !== r.dateFormat;

  const previewMoney = new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 2 }).format(1234.5);
  const previewDate = (() => {
    const d = new Date(2026, 9, 4), p2 = (x) => String(x).padStart(2, "0"), y = 2026, mo = 10, day = 4;
    switch (dateFormat) {
      case "long": return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
      case "us": return `${p2(mo)}/${p2(day)}/${y}`;
      case "eu": return `${p2(day)}/${p2(mo)}/${y}`;
      case "iso": return `${y}-${p2(mo)}-${p2(day)}`;
      default: return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    }
  })();

  const save = () => {
    setRegion({ currency, dateFormat });
    toast.success("Regional settings saved — applying across OWEverit");
    setTimeout(() => window.location.reload(), 700);
  };

  return (
    <div className="space-y-4">
      <Card title="Currency" desc="Used for every amount shown across the app." testid="settings-currency-card">
        <div className="flex items-center gap-3 flex-wrap">
          <select value={currency} onChange={e => setCurrency(e.target.value)} data-testid="settings-currency-select"
            className="px-3 py-2 rounded-lg bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40 min-w-[220px]">
            {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.label} ({c.symbol} · {c.code})</option>)}
          </select>
          <span className="text-sm text-muted-foreground">Preview: <span className="font-mono font-semibold text-foreground" data-testid="settings-currency-preview">{previewMoney}</span></span>
        </div>
      </Card>

      <Card title="Date format" desc="How due dates and payoff dates are displayed. Dates are read as calendar days — no more off-by-one." testid="settings-dateformat-card">
        <div className="flex items-center gap-3 flex-wrap">
          <select value={dateFormat} onChange={e => setDateFormat(e.target.value)} data-testid="settings-dateformat-select"
            className="px-3 py-2 rounded-lg bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40 min-w-[220px]">
            {DATE_FORMATS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
          <span className="text-sm text-muted-foreground">Preview: <span className="font-mono font-semibold text-foreground" data-testid="settings-dateformat-preview">{previewDate}</span></span>
        </div>
      </Card>

      <button onClick={save} disabled={!dirty} data-testid="settings-regional-save"
        className="px-4 py-2 rounded-lg bg-teal-500 text-white text-sm font-semibold hover:bg-teal-600 disabled:opacity-40 transition">Save regional settings</button>
    </div>
  );
}

function toCSV(rows) {
  if (!rows.length) return "";
  const cols = Object.keys(rows[0]);
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [cols.join(","), ...rows.map(r => cols.map(c => esc(r[c])).join(","))].join("\n");
}

function download(name, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

function DataSection() {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState("");

  const exportCsv = async (type) => {
    try {
      const { data } = await api.get(`/${type}`);
      if (!data.length) { toast.info(`No ${type} to export yet`); return; }
      download(`oweverit-${type}-${new Date().toISOString().slice(0, 10)}.csv`, toCSV(data));
      toast.success(`${type[0].toUpperCase() + type.slice(1)} exported`);
    } catch { toast.error("Export failed"); }
  };

  const reset = async () => {
    if (!window.confirm("Replace your current data with the demo dataset? This clears your bills and debts.")) return;
    setBusy(true);
    try { const { data } = await api.post("/seed"); toast.success(`Loaded ${data.bills} demo items`); setTimeout(() => window.location.reload(), 600); }
    catch { toast.error("Reset failed"); } finally { setBusy(false); }
  };

  const deleteAccount = async () => {
    setBusy(true);
    try {
      await api.delete("/account");
      localStorage.removeItem("bf_session_token");
      toast.success("Account deleted");
      setTimeout(() => { window.location.href = "/login"; }, 600);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete account"); setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Card title="Export your data" desc="Download a CSV of your bills or debts for spreadsheets, backups or taxes." testid="settings-export-card">
        <div className="flex gap-3 flex-wrap">
          <button onClick={() => exportCsv("bills")} data-testid="settings-export-bills" className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition"><Download className="h-4 w-4" /> Export bills</button>
          <button onClick={() => exportCsv("debts")} data-testid="settings-export-debts" className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition"><Download className="h-4 w-4" /> Export debts</button>
        </div>
      </Card>

      <Card title="Demo data" desc="Reset your account to the sample bills and debts — handy for exploring features." testid="settings-demo-card">
        <button onClick={reset} disabled={busy} data-testid="settings-reset-btn" className="flex items-center gap-2 px-4 py-2 rounded-lg border border-teal-500/40 text-teal-600 dark:text-teal-300 text-sm font-semibold hover:bg-teal-500/10 disabled:opacity-50 transition"><RefreshCw className="h-4 w-4" /> Load demo data</button>
      </Card>

      <section className="p-6 rounded-2xl border border-rose-500/30 bg-rose-500/5" data-testid="settings-danger-card">
        <div className="font-display font-bold text-lg text-rose-500">Delete account</div>
        <div className="text-xs text-muted-foreground mt-1">Permanently removes your account and all bills, debts, payments and plans. This cannot be undone.</div>
        <div className="mt-5 flex items-center gap-3 flex-wrap">
          <input value={confirm} onChange={e => setConfirm(e.target.value)} placeholder='Type DELETE to confirm' data-testid="settings-delete-confirm"
            className="px-3 py-2 rounded-lg bg-background border border-border text-sm font-mono focus:outline-none focus:ring-2 focus:ring-rose-500/40" />
          <button onClick={deleteAccount} disabled={busy || confirm !== "DELETE"} data-testid="settings-delete-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-500 text-white text-sm font-semibold hover:bg-rose-600 disabled:opacity-40 transition"><Trash2 className="h-4 w-4" /> Delete my account</button>
        </div>
      </section>
    </div>
  );
}
